/**
 * Save-button gating logic for the EventModal.
 *
 * Extracted from the component's JSX so the multi-clause "is the Save/Update
 * button disabled" predicate is named, testable, and keeps the component file
 * under the length standard.
 */
import { UnscheduledEvent } from '../../types';
import { EVENT_STATUS, CAT_MANUAL_DUE_DATE } from '../../constants';
import {
  validateErts,
  validateNextAdvise,
  validateWithinEventWindow,
  validateAuditEntry,
  equalsTimeDeclared,
  TimeRuleContext,
} from './timeValidation';
import { isDateTimeIncomplete } from './dateTimeUtils';

/** Everything the save gate needs to decide whether the button is disabled. */
export interface SaveGateState {
  mode: 'view' | 'edit' | 'create';
  recordType?: string;
  formData: UnscheduledEvent;
  defects: { id: number; text: string }[];
  noteEntries: { date?: string; time?: string; carriedForward?: boolean }[];
  barriers: { startTime?: string; endTime?: string }[];
  auditEntries: { date?: string; time?: string }[];
  selectedChapters: Record<number, string>;
  selectedSections: Record<number, string>;
  /** Newly added manual defects that must have ATA selections before persistence. */
  requiredAtaDefectIds?: number[];
  defectHistory: Record<number, unknown[]>;
  isDirty: boolean;
  auditCompleted: boolean;
  summaryRequired: boolean;
  summaryCompleted: boolean;
  /** Currently selected damage code (modal-local state, not yet on formData). */
  damageCode: string;
  /** Whether an ERTS edit is in progress this session. */
  ertsEdited: boolean;
  /** Confidence selected for the in-progress ERTS edit. */
  ertsConfidence: string;
  /** Change reason selected for the in-progress ERTS edit. */
  ertsChangeReason: string;
  /** Blocks creating a duplicate In-Work Unscheduled event for the same tail. */
  duplicateInWorkBlocked: boolean;
  /** Next Advise as it was when the modal opened — lets rule 3 (the 90-minute
   * past limit) apply only when the field is actually changed this session. */
  originalNextAdvise?: string;
  /** Deferred: an existing Due Date was revised this session (needs a reason). */
  dueDateChangedFromExisting?: boolean;
  /** Deferred: the change reason selected for the Due Date revision. */
  dueDateChangeReason?: string;
  /**
   * True when the record is Completed with a Damage IROP, a correlating SkyDent
   * record exists, and its Damage Location is still blank — required before a
   * Completed damage record can be updated.
   */
  damageLocationRequiredMissing?: boolean;
  /**
   * True when the gate is being evaluated for a Move (the source is closed as
   * COMPLETED). Moves are exempt from the completion-only final-ERTS rule so the
   * existing move workflow is unchanged.
   */
  isMoveClose?: boolean;
}

/**
 * True when the Damage IROP is flagged but no damage code has been chosen yet.
 */
function damageCodeMissing(state: SaveGateState): boolean {
  return !!state.formData.damage && !state.damageCode;
}

/**
 * Minimum number of characters required in the Corrective Action before a
 * Completed record can be saved.
 */
export const CORRECTIVE_ACTION_MIN_LENGTH = 30;

/**
 * True when the record is Completed but the Corrective Action has fewer than
 * the required minimum number of characters.
 */
function correctiveActionMissing(state: SaveGateState): boolean {
  const isCompleted = state.formData.status === EVENT_STATUS.COMPLETED;
  return (
    isCompleted &&
    (state.formData.correctiveAction || '').trim().length < CORRECTIVE_ACTION_MIN_LENGTH
  );
}

/**
 * True when every defect has both an ATA chapter and section selected.
 */
function allDefectsClassified(state: SaveGateState): boolean {
  return state.defects.every((d) => state.selectedChapters[d.id] && state.selectedSections[d.id]);
}

/** True when every specified defect has both required ATA selections. */
function requiredDefectsClassified(state: SaveGateState, defectIds: number[]): boolean {
  return defectIds.every((defectId: number): boolean =>
    Boolean(state.selectedChapters[defectId] && state.selectedSections[defectId]),
  );
}

/**
 * True when every defect has at least one defect-history record.
 */
function allDefectsHaveHistory(state: SaveGateState): boolean {
  return state.defects.every(
    (d) => state.defectHistory[d.id] && state.defectHistory[d.id].length > 0,
  );
}

/**
 * True when Time Declared is required but empty. Required for Unscheduled,
 * Planned, and Deferred records (Scheduled handles its Schedule Date in
 * scheduledIncomplete). Applies in create and edit.
 */
function timeDeclaredMissing(state: SaveGateState): boolean {
  if (state.mode === 'view') return false;
  const requires =
    state.recordType === 'Unscheduled' ||
    state.recordType === 'Planned' ||
    state.recordType === 'Deferred';
  return requires && !state.formData.timeDeclaredZulu;
}

/**
 * Validates each note's date/time against the event window. Notes carried
 * forward by a Move are skipped (their original timestamps may predate the
 * destination's Time Declared, and were already valid on the source). Deferred
 * records lift the not-before-Time-Declared rule entirely. Also enforces that at
 * most one note may equal the Time Declared.
 */
function noteWindowReasons(state: SaveGateState, context: TimeRuleContext): string[] {
  const reasons: string[] = [];
  let notesEqualToTimeDeclared = 0;
  const notesContext =
    state.recordType === 'Deferred' ? { ...context, timeDeclared: undefined } : context;
  for (const note of state.noteEntries) {
    if (!note.date || !note.time || note.carriedForward) continue;
    const combined = `${note.date} ${note.time}`;
    if (equalsTimeDeclared(combined, state.formData.timeDeclaredZulu))
      notesEqualToTimeDeclared += 1;
    // A note may equal the Time Declared (the earliest note), so allow equality
    // here; the "only one match" constraint is enforced by the count below.
    const noteError = validateWithinEventWindow(combined, notesContext, {
      allowEqualToTimeDeclared: true,
    });
    if (noteError) {
      reasons.push(`A Note date/time — ${noteError}`);
      break;
    }
  }
  if (state.recordType !== 'Deferred' && notesEqualToTimeDeclared > 1) {
    reasons.push('Only one Note may match the Time Declared; the rest must be later.');
  }
  return reasons;
}

/**
 * Returns specific reasons for any cross-field temporal rule violation, naming
 * the element at fault (ERTS, Next Advise, a barrier, a note, or an audit
 * entry) so the user can find it — barriers/notes/audit entries are edited in
 * their own popups and aren't highlighted inline in the main view. Each rule:
 * times must be later than the Time Declared, and once Completed, Next Advise,
 * barriers and notes must not be after the ERTS. Incomplete values are ignored.
 */
function temporalRuleReasons(state: SaveGateState): string[] {
  if (state.mode === 'view') return [];
  const { formData } = state;
  const context: TimeRuleContext = {
    timeDeclared: formData.timeDeclaredZulu,
    erts: formData.returnToServiceTimeZulu,
    isCompleted: formData.status === EVENT_STATUS.COMPLETED,
    completedByAliasLogged: !!formData.completedByAlias,
  };
  const reasons: string[] = [];

  const ertsError = validateErts(formData.returnToServiceTimeZulu, context);
  if (ertsError) reasons.push(`ERTS — ${ertsError}`);

  const nextAdviseEdited = formData.nextAdviseTimeZulu !== state.originalNextAdvise;
  const nextAdviseError = validateNextAdvise(formData.nextAdviseTimeZulu, context, {
    enforcePastLimit: nextAdviseEdited,
  });
  if (nextAdviseError) reasons.push(`Next Advise — ${nextAdviseError}`);

  for (const barrier of state.barriers) {
    const barrierError =
      validateWithinEventWindow(barrier.startTime, context, { allowEqualToTimeDeclared: true }) ||
      (barrier.endTime ? validateWithinEventWindow(barrier.endTime, context) : null);
    if (barrierError) {
      reasons.push(`A Maintenance Barrier time — ${barrierError}`);
      break;
    }
  }

  for (const reason of noteWindowReasons(state, context)) reasons.push(reason);

  for (const audit of state.auditEntries) {
    if (!audit.date || !audit.time) continue;
    const auditError = validateAuditEntry(`${audit.date} ${audit.time}`, context);
    if (auditError) {
      reasons.push(`An Audit entry date/time — ${auditError}`);
      break;
    }
  }

  return reasons;
}

/**
 * Missing-field reasons specific to a Deferred record: one deferral category,
 * a CAT type, and a manual Due Date for the CAT types that require one.
 */
function deferredFieldReasons(formData: UnscheduledEvent): string[] {
  const reasons: string[] = [];
  const categorySelected = !!(
    formData.apuDeferral ||
    formData.etopsRestricted ||
    formData.operationallyRestrictive ||
    formData.catIiiRestricted
  );
  if (!categorySelected) reasons.push('Select a Deferred category.');
  if (!formData.catType) reasons.push('Select a CAT type.');
  if (CAT_MANUAL_DUE_DATE.includes(formData.catType || '') && !formData.dueDateZulu) {
    reasons.push('Enter the Due Date.');
  }
  return reasons;
}

/**
 * Missing-field reasons specific to a Scheduled record. Scheduled records carry
 * their Schedule Date in `timeDeclaredZulu` and require a complete ERTS and
 * Next Advise up front rather than at completion.
 */
function scheduledFieldReasons(formData: UnscheduledEvent): string[] {
  const reasons: string[] = [];
  if (!formData.timeDeclaredZulu) reasons.push('Enter the Schedule Date.');
  if (!(formData.station || '').trim()) reasons.push('Enter the station.');
  if (isDateTimeIncomplete(formData.returnToServiceTimeZulu))
    reasons.push('Enter a complete ERTS date and time.');
  if (isDateTimeIncomplete(formData.nextAdviseTimeZulu))
    reasons.push('Enter a complete Next Advise date and time.');
  return reasons;
}

/**
 * Defect, ATA-classification, and note reasons. A record requiring standard
 * classification must have at least one defect and note with every defect
 * classified; otherwise only the explicitly required defect ids need an ATA.
 */
function classificationReasons(
  state: SaveGateState,
  requiresStandardClassification: boolean,
): string[] {
  const reasons: string[] = [];
  if (requiresStandardClassification) {
    if (state.defects.length === 0) reasons.push('Add at least one defect.');
    else if (!allDefectsClassified(state))
      reasons.push('Select an ATA chapter and section for every defect.');
    if (state.noteEntries.length === 0) reasons.push('Add at least one note.');
    return reasons;
  }
  if (!requiredDefectsClassified(state, state.requiredAtaDefectIds || [])) {
    reasons.push('Select an ATA chapter and section for every defect.');
  }
  return reasons;
}

/**
 * Collects the missing-field reasons that depend on the record type (defects,
 * ATA classification, defect history, notes, and the Deferred/Scheduled-only
 * fields). Kept separate so {@link getSaveDisabledReasons} stays small.
 */
function recordTypeReasons(state: SaveGateState): string[] {
  const { mode, recordType } = state;
  const f = state.formData;
  const editable = mode !== 'view';
  const requiresFullDetail =
    mode === 'create' && (recordType === 'Unscheduled' || recordType === 'Planned');
  const isDeferred = recordType === 'Deferred';
  const isScheduled = recordType === 'Scheduled';
  const requiresStandardClassification =
    requiresFullDetail || (isDeferred && editable) || (isScheduled && editable);

  const reasons: string[] = classificationReasons(state, requiresStandardClassification);

  if (requiresFullDetail && isDateTimeIncomplete(f.nextAdviseTimeZulu))
    reasons.push('Enter a complete Next Advise date and time.');
  if (isDeferred && editable) reasons.push(...deferredFieldReasons(f));
  if (isScheduled && editable) reasons.push(...scheduledFieldReasons(f));
  if (recordType !== 'Scheduled' && state.defects.length > 0 && !allDefectsHaveHistory(state)) {
    reasons.push('Add defect history for every defect.');
  }
  return reasons;
}

/**
 * Reasons that only apply once the record is being closed as Completed: the
 * Audit and Summary must be done and no Maintenance Barrier may still be open.
 */
function completionGateReasons(state: SaveGateState): string[] {
  const isCompleted = state.formData.status === EVENT_STATUS.COMPLETED;
  const reasons: string[] = [];
  if (state.mode === 'edit' && isCompleted && !state.auditCompleted)
    reasons.push('Complete the Audit.');
  if ((state.summaryRequired || isCompleted) && !state.summaryCompleted)
    reasons.push('Generate the Summary.');
  // A Completed event may not carry an unresolved Maintenance Barrier — every
  // barrier must have an end time (be closed) before the event can be updated.
  if (isCompleted && state.barriers.some((barrier) => !barrier.endTime)) {
    reasons.push('Close all open Maintenance Barriers before completing.');
  }
  return reasons;
}

/**
 * Reasons tied to the ERTS and Due Date change ceremonies: an edit needs a
 * confidence and change reason, completion finalizes the ERTS, and a partially
 * entered ERTS must never persist.
 */
function ertsCeremonyReasons(state: SaveGateState): string[] {
  const { formData, recordType } = state;
  const isCompleted = formData.status === EVENT_STATUS.COMPLETED;
  const ertsIncomplete = isDateTimeIncomplete(formData.returnToServiceTimeZulu);
  const reasons: string[] = [];
  if (state.ertsEdited && (!state.ertsConfidence || !state.ertsChangeReason))
    reasons.push('Select an ERTS confidence and change reason.');
  // Completing a non-Deferred record finalizes its ERTS, so a complete ERTS
  // date and time must be present before the event can be closed. Moves are
  // exempt (their existing gating is preserved).
  if (isCompleted && !state.isMoveClose && recordType !== 'Deferred' && ertsIncomplete) {
    reasons.push('Enter the final ERTS date and time before completing.');
  }
  if (state.dueDateChangedFromExisting && !state.dueDateChangeReason)
    reasons.push('Select a Due Date change reason.');
  // A started-but-incomplete ERTS (e.g. a time with no date) must not persist.
  // Empty ERTS is allowed (optional / removed); only a partial one is blocked.
  if (state.ertsEdited && formData.returnToServiceTimeZulu && ertsIncomplete) {
    reasons.push('Enter a complete ERTS date and time.');
  }
  return reasons;
}

/**
 * Returns a human-readable list of every condition still blocking the
 * Save/Update button. An empty array means the button is active. Used both to
 * gate the button ({@link isSaveDisabled}) and to populate the hover popup that
 * explains what remains. Messages are de-duplicated.
 */
export function getSaveDisabledReasons(state: SaveGateState): string[] {
  const { mode, formData } = state;
  if (formData.status === EVENT_STATUS.DELETED) return [];

  const reasons: string[] = [];
  const add = (message: string): void => {
    if (!reasons.includes(message)) reasons.push(message);
  };

  if (mode === 'create' && !state.recordType) add('Select a record type.');
  if (mode === 'create' && !formData.status) add('Select a status.');
  if (state.duplicateInWorkBlocked)
    add('An In-Work Unscheduled event already exists for this tail.');
  if (!formData.tailNumber) add('Enter a tail number.');
  if (timeDeclaredMissing(state)) add('Enter the Time Declared (date and time).');
  for (const reason of recordTypeReasons(state)) add(reason);
  for (const reason of temporalRuleReasons(state)) add(reason);
  if (mode === 'edit' && !state.isDirty) add('Make a change before updating.');
  for (const reason of completionGateReasons(state)) add(reason);
  if (damageCodeMissing(state)) add('Select a damage code.');
  if (state.damageLocationRequiredMissing) add('Complete the SkyDent Damage Location.');
  // A checked Delay IROP shows the ONGO placeholder until the actual minutes are
  // entered; ONGO counts as populated, so the Delay duration never blocks save.
  if (correctiveActionMissing(state))
    add(`Enter at least ${CORRECTIVE_ACTION_MIN_LENGTH} characters in Corrective Action.`);
  for (const reason of ertsCeremonyReasons(state)) add(reason);

  return reasons;
}

/**
 * Determines whether the Save/Update button should be disabled.
 */
export function isSaveDisabled(state: SaveGateState): boolean {
  return getSaveDisabledReasons(state).length > 0;
}
